/**
 * DrawerTaskPanel — 数据转换层
 *
 * 将后端 CreationDetail（含 assetList + taskList）转换为 UI 层使用的 CombinedTaskItem。
 *
 * 主要职责：
 * - formatTitleField：根据 cloudTaskType 和 extendJson 解析展示标题与模型名
 * - mapAssetsToMediaItemsStatic / mapTasksToMediaItemsStatic：资产/任务 → MediaItem
 * - pickPromptFromDetailStatic：从 taskExtraParam 中提取 prompt 和预览图
 *   · 视频/图片生成类：解析嵌套 JSON params.prompt / params.image
 *   · 爆款复刻/模板类：解析 productName / previewUrl
 * - getBatchStateStatic：根据子任务状态推导批次整体状态
 * - transformCreationDetailToCombined：组合以上函数，输出完整的 CombinedTaskItem
 */
import { ModelTypeEnum, ModelTypeNameMap } from '@/utils/const';
import { F_CLOUD_TASK_TYPE } from '@/utils/const';
import { t } from '@/utils/helper/intl';
import { faceSwapperURL, getFeatureName } from '@/utils/router';

import type { AssetItem, CreationDetail, TaskItem } from './service';
import type { CombinedTaskItem, MediaItem } from './useTaskList';
import { MediaType, TaskState } from './useTaskList';

const getParamsByModelType = (extendJson: string, defaultText: string) => {
  const result = { title: defaultText, tags: [] as string[], modelName: '' };
  if (!extendJson) return result;
  try {
    const taskExtraParam = JSON.parse(JSON.parse(extendJson)?.taskExtraParam);
    const { modelType, modelName } = taskExtraParam;
    result.title = ModelTypeNameMap[modelType as ModelTypeEnum] || defaultText;
    result.modelName = modelName;
    return result;
  } catch {
    return result;
  }
};

function formatTitleField(detail: CreationDetail, pathname?: string) {
  let title = '';
  const tags: string[] = [];
  let modelName = '';

  const { cloudTaskType, extendJson } = detail;

  if (pathname === faceSwapperURL) {
    title = getFeatureName(pathname);
    if (cloudTaskType === F_CLOUD_TASK_TYPE.F_HEAD_SWAP) {
      tags.push('v2');
    } else {
      tags.push('v1');
    }
  } else if (
    [F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE, F_CLOUD_TASK_TYPE.F_VIDEO_GENERATE].includes(
      cloudTaskType!,
    )
  ) {
    return getParamsByModelType(
      extendJson!,
      cloudTaskType === F_CLOUD_TASK_TYPE.F_VIDEO_GENERATE
        ? t('9a1641a16a4cc4df9ffa2a17b6348ae0')
        : t('c7741980e9f14c014b9e2182050da31e'),
    );
  } else if (F_CLOUD_TASK_TYPE.F_VIDEO_EDIT === cloudTaskType) {
    const result = getParamsByModelType(
      extendJson!,
      t('9613af8a82f375ffce54844fc0a470cc'),
    );
    !result.modelName && (result.modelName = 'Kling Video O1 Std Video Edit');
    return result;
  } else if (cloudTaskType === F_CLOUD_TASK_TYPE.F_TEXT_TO_SPEECH) {
    title = t('merror.sub.tts');
  } else if (cloudTaskType === F_CLOUD_TASK_TYPE.F_VOICE_CLONE) {
    title = t('ce24a821ce9a84f071a4b291570d64b6');
  } else if (cloudTaskType === F_CLOUD_TASK_TYPE.F_VIRAL_TEMPLATE) {
    title = t('c09832dfa0512af3b329a64c65ccf061');
  } else if (pathname) {
    title = getFeatureName(pathname);
  }

  return {
    title,
    tags,
    modelName,
  };
}

export function mapAssetsToMediaItemsStatic(assets: AssetItem[] = []): MediaItem[] {
  return assets.map((asset) => ({
    id: asset.id,
    title: asset.title ?? (asset.extendInfo?.videoTitle || ''),
    url: asset.fileUrl,
    cover: asset.cover,
    duration: asset.duration,
    state: TaskState.SUCCESS,
    mediaType: asset.type as MediaType,
    rawData: asset,
  }));
}

export function mapTasksToMediaItemsStatic(tasks: TaskItem[] = []): MediaItem[] {
  return tasks.map((task) => ({
    id: task.id,
    title: task.videoTitle ?? (task.extendInfo?.title || ''),
    url: task.videoUrl,
    cover: task.videoPreviewUrl,
    state: task.state as TaskState,
    mediaType: task.taskType as MediaType,
    rawData: task,
  }));
}

export function pickPromptFromDetailStatic(
  detail: CreationDetail,
  type: F_CLOUD_TASK_TYPE,
) {
  const { assetList = [], taskList = [] } = detail;
  const sourceTask = [...assetList, ...taskList].find((task) => {
    try {
      !task.extendInfo && (task.extendInfo = {});
      const { taskExtraParam } = task.extendInfo!;
      if (
        [
          F_CLOUD_TASK_TYPE.F_VIDEO_GENERATE,
          F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE,
          F_CLOUD_TASK_TYPE.F_VIDEO_EDIT,
        ].includes(type)
      ) {
        if (!taskExtraParam) return false;
        const params = JSON.parse(JSON.parse(taskExtraParam)?.params);
        const { prompt, image, images } = params;
        if (prompt || image || images?.length) {
          task.extendInfo!.prompt = prompt;
          task.extendInfo!.promptImage = images || image;
          return true;
        }
        return false;
      }
      if (
        type === F_CLOUD_TASK_TYPE.F_VIRAL_REMAKE ||
        type === F_CLOUD_TASK_TYPE.F_VIRAL_TEMPLATE
      ) {
        if (taskExtraParam) {
          const { productName, previewUrl } = JSON.parse(taskExtraParam);
          task.extendInfo!.prompt = productName;
          task.extendInfo!.promptImage = previewUrl;
        }
        return !!(task.extendInfo!.prompt || task.extendInfo!.promptImage);
      }
      return false;
    } catch {
      return false;
    }
  });

  return {
    prompt: sourceTask?.extendInfo?.prompt,
    promptImage: sourceTask?.extendInfo?.promptImage,
  };
}

export function getBatchStateStatic(detail: CreationDetail): TaskState {
  const tasks = detail.taskList || [];

  if (tasks.length === 0) {
    return detail.assetList && detail.assetList.length > 0
      ? TaskState.SUCCESS
      : TaskState.SUCCESS;
  }

  if (tasks.some((task) => task.state === TaskState.PENDING)) {
    return TaskState.PENDING;
  }

  if (tasks.some((task) => task.state >= TaskState.FAILED)) {
    return TaskState.FAILED;
  }

  return TaskState.SUCCESS;
}

/** 将 CreationDetail 转换为 UI 层的 CombinedTaskItem */
export function transformCreationDetailToCombined(
  detail: CreationDetail,
  pathname: string | undefined,
  fallbackIndex: number,
): CombinedTaskItem {
  const firstTask = detail.taskList?.[0];
  const firstAsset = detail.assetList?.[0];
  const cloudTaskType =
    detail.cloudTaskType ?? firstTask?.cloudTaskType ?? firstAsset?.cloudTaskType;

  const mediaItems = [
    ...mapTasksToMediaItemsStatic(detail.taskList || []),
    ...mapAssetsToMediaItemsStatic(detail.assetList || []),
  ];
  const { prompt, promptImage } = pickPromptFromDetailStatic(
    detail,
    cloudTaskType ?? F_CLOUD_TASK_TYPE.F_VIRAL_TEMPLATE,
  );
  const taskId = detail.taskId;
  const { title, tags, modelName } = formatTitleField(detail, pathname);
  const createTime = detail.createTime ?? Date.now();
  const id = detail.taskId;

  return {
    id,
    taskId,
    title,
    tags,
    createTime,
    cloudTaskType,
    state: getBatchStateStatic(detail),
    mediaItems,
    prompt,
    promptImage,
    modelName,
    rawData: detail,
  };
}
